// 3rd party libs
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  UserFormElement,
  UserGroup,
  UserManagementTableType,
  UserTypes,
  UserValidationState
} from 'types/user-management';
import styled, { useTheme } from 'styled-components';

// Components

import { Button, Flyout, FlyoutHeader } from 'components';
import UserForm, {
  FormSection,
  InputContainer,
  StyledInputLabel
} from 'pages/UserManagementTwo/components/User/AddEditUser/UserForm';

// Types
import { Id } from 'types';
import {
  useCreateUserMutation,
  useGetUserManagementV2Query,
  useLazyGetUserManagementV2Query
} from 'api';
import { DropDownItem } from 'pages/ProteinMachine/MachineConfig/Common/Alerts/FormElementsTypes';
import { ActionMeta, InputActionMeta, MultiValue } from 'react-select';
import { toSnakeCase } from 'common/helpers/v1';
import { ToastMsg } from 'common/components/Toast';
import { GroupHeaderPropt } from '../../Group/AddEditGroup/GroupInfo';
import { useTranslation } from 'react-i18next';
import Collapse from 'rc-collapse';
import {
  expandIcon,
  MenuPanel
} from 'pages/MaintenanceServiceDashboard/MaintenanceQueryTableByFrequency';
import { v4 as uuidv4 } from 'uuid';
import { AddPlusIcon } from 'icons/indexed-icons/AddPlusIcon';
import { SectionRule } from '../../Group/AddEditGroup/GroupInfo';
import { IcoTrash } from 'icons/indexed-icons/IcoTrash';
import CustomAsyncSelect from 'components/CustomSelect/CustomAsyncSelect';
import theme from 'themes';
import ConfirmationModal from 'common/components/ConfirmationModal';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';
import { toLower } from 'lodash';

const Container = styled.div`
  background: ${({ theme }) => theme.colors.white};
  height: 100%;
  outline: none;
  padding-bottom: 9rem;
  overflow: auto;
`;
const UserFormContainer = styled.div`
  padding: 0 1.5rem 1.125rem;

  .user-collapse-panel > .rc-collapse-item {
    padding-bottom: 0.375rem;
    border-bottom: 0.063rem solid ${theme.colors.lightGrey4};
    &:first-child {
      padding-top: 0.5rem;
    }
    &:last-child {
      border: none;
      margin-bottom: 0.25rem;
    }
  }
`;
export const Footer = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  box-shadow: 0 0.75rem 1.5rem 0.75rem rgba(0, 0, 0, 0.05);
  border-top: 0.063rem solid ${({ theme }) => theme.colors.lightGrey10};
  background: ${({ theme }) => theme.colors.white};
  height: 4rem;
  flex-shrink: 0;
  &.add-group-footer {
    margin: 0 -8px;
  }
  &.service-footer {
    margin-left: -16px;
    width: 28rem;
  }
`;
export const BtnContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  padding-right: 1.25rem;
  align-items: center;
`;
const AddAnotheuserContainer = styled.div`
  display: flex;
  margin-top: 0.25rem;
  margin-bottom: 1.25rem;
  cursor: pointer;
`;
const AddAnotheuser = styled.div`
  color: ${({ theme }) => theme.colors.text.lightBlack};
  font-family: Roboto;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-left: 0.75rem;
`;
const HeaderWrapper = styled.div`
  display: flex;
  margin-left: 1.25rem;
`;
const ChildHeader = styled.div`
  font-family: Roboto;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  color: ${({ theme }) => theme.colors.text.lightBlack};
`;
const HeaderIcon = styled.div`
  margin-left: auto;
  cursor: pointer;
`;
// Types
interface Props {
  tableType: UserManagementTableType;
  tableItemId?: Id;
  open: boolean;
  setIsOpenModal: () => void;
  selectedItem?: UserTypes;
  setSelectedItem?: React.Dispatch<React.SetStateAction<UserTypes | undefined>>;
}
const defaultUserField = {
  email: '',
  firstName: '',
  lastName: '',
  textAlert: false,
  emailAlert: false,
  phone: '',
  groups: [],
  invalidEmail: true
};
const UserManagementModal = ({
  tableType,
  tableItemId,
  open,
  setIsOpenModal,
  selectedItem = defaultUserField,
  setSelectedItem
}: Props): ReactElement => {
  const { t, i18n } = useTranslation(['common']);
  const theme = useTheme();
  const [isViewConfirmCancel, setIsViewConfirmCancel] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [hasUniqueUserEmails, setHasUniqueUserEmails] = useState<boolean>(true);
  const [isValidGroupSelection, setIsValidGroupSelection] = useState<boolean>(false);
  const [multiValue, setMultiValue] = useState<DropDownItem[]>([]);
  const [groupListOffset, setGroupListOffset] = useState<number>(0);
  const [displayedGroups, setDisplayedGroups] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [newUsers, setNewUsers] = useState<UserFormElement[]>([
    { user: defaultUserField, validationState: false, id: uuidv4() }
  ]);
  const [createUser] = useCreateUserMutation();

  const [getGroupList] = useLazyGetUserManagementV2Query();
  const { data: groups } = useGetUserManagementV2Query({
    type: 'group',
    limit: 50,
    offset: groupListOffset
  });
  const getUserPanelKeys = (): string[] => {
    const keys: string[] = [];
    newUsers.forEach((panel) => {
      keys.push(panel.id as string);
    });
    return keys;
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<UserGroup[]>([]);
  const [activePanelKeys, setActivePanelKeys] = useState<React.Key[]>(getUserPanelKeys());
  const [isCleanForm, setIsCleanForm] = useState<boolean>(true);
  const [input, setInput] = useState<string>('');

  const groupsOpt = useMemo(() => {
    const list = groups?.items.map((group) => ({ label: group.name, value: group.id })) || [];
    const totalGroups = [...displayedGroups, ...list];
    return totalGroups;
  }, [groups]);

  const unselectedGroup = useMemo(() => {
    const unselectedData =
      [...displayedGroups]?.filter(
        (group) => !multiValue.find((selected) => selected?.value === group.value)
      ) || [];
    return unselectedData;
  }, [multiValue]);

  useMemo(() => {
    const selectedGroups = multiValue.length
      ? multiValue
      : selectedItem?.groups?.map((group) => ({ label: group.name || '', value: group.id })) || [];
    const unselectedGroups =
      groupsOpt?.filter(
        (group) => !selectedGroups.find((selected) => selected?.value === group.value)
      ) || [];

    setDisplayedGroups(() => [...selectedGroups, ...unselectedGroups]);
  }, [groupsOpt, selectedItem]);

  useEffect(() => {
    if (selectedItem.id) {
      setIsEditMode(true);
      setNewUsers([{ user: selectedItem, validationState: false, id: selectedItem.id }]);
    } else {
      setIsEditMode(false);
    }
    if (selectedItem.groups) {
      const selectedGroups =
        selectedItem.groups?.map((group) => ({ value: group.id, label: group.name || '' })) ?? [];
      setMultiValue(selectedGroups);
      setSelectedGroups([...selectedItem.groups]);
    }
  }, [selectedItem, selectedItem.groups]);

  const isGroupChanged = (selectedGroups: { id: string }[]): boolean => {
    if (!selectedItem.group && selectedGroups.length > 0) return true;
    else if (!selectedItem.group && selectedGroups.length === 0) return false;
    const existingGroups = selectedItem.groups;
    const selectedGroupIds = selectedGroups.map((group) => group.id);
    const isChanged =
      existingGroups?.length !== selectedGroupIds.length ||
      existingGroups?.some((group) => !selectedGroupIds.includes(group.id));
    return isChanged;
  };

  useEffect(() => {
    validateNoneActiveForms();
  }, [newUsers]);
  useEffect(() => {
    if (tableItemId === undefined) {
      let isClean = true;
      newUsers.forEach((user) => {
        isClean = isClean && formHasNoUnsavedData(user.user);
      });
      setIsCleanForm(isClean && multiValue.length === 0);
    }
  }, [newUsers, multiValue]);
  const getAdminStatus = (groupId: string): boolean => {
    const item = selectedItem.group?.find((grp) => grp.id === groupId);
    if (item) {
      return item.isAdmin as boolean;
    }
    return false;
  };
  const handleInputOnChange = (e: MultiValue<DropDownItem>, option: ActionMeta<DropDownItem>) => {
    if (option.removedValue && option.removedValue.isFixed) return;
    if (isEditMode) setIsValid(true);
    setMultiValue([...e]);
    const selectedGroups = e.map((group) => ({
      id: String(group.value),
      name: group.label,
      isAdmin: getAdminStatus(String(group.value))
    }));

    setIsValidGroupSelection(!!selectedGroups.length && isGroupChanged(selectedGroups));

    setSelectedGroups([...selectedGroups]);
  };

  const onClose = () => {
    onCloseVerifyData();
  };
  const loadOptions = async (inputValue: string) => {
    const groups = await getGroupList({
      type: 'group',
      nameSearchQuery: inputValue.trim()
    });
    const groupList = groups.data?.items.map((group) => ({ label: group.name, value: group.id }));
    const selectedGroups =
      selectedItem.groups?.map((group) => ({ value: group.id, label: group.name || '' })) ?? [];
    const unselectedGroups =
      groupList?.filter(
        (group) => !selectedGroups.find((selected) => selected?.value === group.value)
      ) || [];
    return Promise.resolve(unselectedGroups || []);
  };

  const massageUserData = (): UserTypes[] => {
    const data: UserTypes[] = [];
    for (const user of newUsers) {
      const u = toSnakeCase(user.user);
      u.groups = (selectedGroups as UserGroup[]).map((grp) => toSnakeCase(grp));
      delete u.group;
      data.push(u);
    }
    return data;
  };
  const handleUserSave = () => {
    const params = massageUserData();
    createUser(params)
      .unwrap()
      .then(() => {
        ToastMsg({
          heading: 'Success',
          message: tableItemId ? 'Data updated successfully' : 'A user has been created.',
          type: 'success',
          position: 'top-center'
        });
        setIsOpenModal();
        setIsValid(false);
        setNewUsers([{ user: defaultUserField, validationState: false, id: uuidv4() }]);
        setMultiValue([]);
      })
      .catch((err) => {
        ToastMsg({
          heading: 'Error',
          message: err.error.message,
          type: 'error',
          position: 'top-center'
        });
      });
  };
  const haveUniqueEmails = (forms: UserFormElement[]): { isUnique: boolean; id: string } => {
    const emailSet = new Set<string>();
    for (const form of forms) {
      if (emailSet.has(toLower(form.user.email as string))) {
        return { isUnique: false, id: form.id as string };
      }
      emailSet.add(toLower(form.user.email as string));
    }
    return { isUnique: true, id: '' };
  };

  const handleFormChange = (data: UserTypes, isValidForm?: boolean, id?: string) => {
    newUsers.forEach((form, index) => {
      if (id && form.id === id) {
        newUsers[index] = { ...form, user: data, validationState: isValidForm as boolean };
      }
    });
    if (isValidForm) {
      const { isUnique, id } = haveUniqueEmails(newUsers);
      setHasUniqueUserEmails(isUnique);
      if (!isUnique) {
        newUsers.forEach((form, index) => {
          if (id && form.id === id) {
            newUsers[index] = {
              ...form,
              user: data,
              validationState: {
                ...(form.validationState as UserValidationState),
                email: 'Email already entered'
              }
            };
          }
        });
      }
    } else {
      setIsValid(isValidForm as boolean);
    }
    setNewUsers([...newUsers]);
    setIsCleanForm(false);
  };
  const formHasNoUnsavedData = (data: UserTypes): boolean => {
    const phone = (data.phone === '' || (data.phone && data.phone?.length < 6)) as boolean;
    return (
      data.email === '' &&
      data.firstName === '' &&
      data.lastName === '' &&
      phone &&
      (!data.textAlert as boolean) &&
      (!data.emailAlert as boolean)
    );
  };
  const validateNoneActiveForms = (): void => {
    const hasInvalidForm = newUsers.some((user) => !user.validationState);
    setIsValid(!hasInvalidForm);
  };
  const onCloseVerifyData = () => {
    if (isCleanForm) {
      closeAndResetFlyoutData();
    } else {
      setIsViewConfirmCancel(true);
    }
  };
  const closeAndResetFlyoutData = () => {
    setNewUsers([{ user: defaultUserField, validationState: false, id: uuidv4() }]);
    setIsOpenModal();
    setMultiValue([]);
    setIsValid(false);
    setIsValidGroupSelection(false);
    setIsCleanForm(true);
    setSelectedItem?.(defaultUserField);
  };
  const flyoutTitle = useMemo(() => {
    return tableItemId ? t('edit_user') : t('add_a_new_user');
  }, [tableType, tableItemId, i18n.language]);

  const helperText = t('unsaved_changes_message');
  const title = t('unsaved_changes_title');
  const addAnotherUser = (): void => {
    const newId: string = uuidv4();
    setNewUsers((prev) => [...prev, { user: defaultUserField, validationState: {}, id: newId }]);
    setActivePanelKeys([newId]);
  };
  const deleteNewUser = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number): void => {
    e.stopPropagation();
    const temp = JSON.parse(JSON.stringify(newUsers));
    temp.splice(index, 1);
    const key: string = temp[temp.length - 1].id as string;
    setActivePanelKeys([key]);
    setNewUsers([...temp]);
  };

  const checkDisable = (): boolean => {
    const isInvalidEmail = newUsers.some(({ user }) => user.invalidEmail);
    if (isInvalidEmail || !selectedGroups.length || !isValid) {
      return true;
    }
    return !(isValid || isValidGroupSelection);
  };
  const onInputChange = (value: string, { action }: InputActionMeta) => {
    if (action === 'input-change') {
      setInput(value);
    }
    if (action === 'set-value') {
      return input;
    }
    if (action === 'menu-close') {
      setInput('');
      return '';
    }
  };

  const sortUnselectedGroups = () => {
    if (!unselectedGroup.length) return;
    const selectedGroups = [...multiValue] as { label: string; value: string }[];
    const sortedData = [...unselectedGroup].sort((group, nextGroup) =>
      nextGroup?.label?.toLowerCase() > group?.label?.toLowerCase() ? -1 : 1
    );
    setDisplayedGroups([...selectedGroups, ...sortedData]);
  };

  return (
    <>
      {isViewConfirmCancel && (
        <ConfirmationModal
          isConfirmPrompt
          isVisible={isViewConfirmCancel}
          type="warning"
          title={title}
          headerIcon={<TriangleWarning />}
          onConfirmCallback={() => {
            setIsOpenModal();
            setIsViewConfirmCancel(false);
            closeAndResetFlyoutData();
          }}
          onCancelCallback={() => {
            setIsViewConfirmCancel(false);
          }}
        >
          <p>{helperText}</p>
        </ConfirmationModal>
      )}
      <Flyout width="28.125rem" visible={open} onClose={onCloseVerifyData}>
        <FlyoutHeader
          heading={flyoutTitle}
          onClose={onCloseVerifyData}
          bgColor={theme.colors.white}
        />
        <Container tabIndex={0}>
          <UserFormContainer>
            <GroupHeaderPropt>{t('by_adding_a_new_user')}</GroupHeaderPropt>
            {newUsers && newUsers.length > 1 ? (
              <>
                <Collapse
                  className="user-collapse-panel"
                  activeKey={activePanelKeys[0]}
                  onChange={(key: React.Key | React.Key[]) => {
                    setActivePanelKeys(Array.isArray(key) ? key : [key]);
                  }}
                  destroyInactivePanel={true}
                  expandIcon={expandIcon}
                  accordion={true}
                >
                  {newUsers.map((panel, index) => {
                    const header = (
                      <HeaderWrapper>
                        <ChildHeader>User &nbsp; {index + 1}</ChildHeader>
                        <HeaderIcon
                          onClick={(e) => {
                            deleteNewUser(e, index);
                          }}
                          title="Delete User"
                        >
                          <IcoTrash width={'30px'} />
                        </HeaderIcon>
                      </HeaderWrapper>
                    );
                    return (
                      <MenuPanel
                        showArrow={true}
                        collapsible={'header'}
                        key={panel.id}
                        header={header}
                        mb={0}
                      >
                        {
                          <UserForm
                            isUserPage
                            handleFormChange={handleFormChange}
                            userData={newUsers[index]}
                          />
                        }
                      </MenuPanel>
                    );
                  })}
                </Collapse>
              </>
            ) : (
              <UserForm isUserPage handleFormChange={handleFormChange} userData={newUsers[0]} />
            )}
            <FormSection>
              {!isEditMode && (
                <AddAnotheuserContainer onClick={addAnotherUser}>
                  <AddPlusIcon /> <AddAnotheuser>Add another User</AddAnotheuser>
                </AddAnotheuserContainer>
              )}
              <SectionRule></SectionRule>
            </FormSection>
            <FormSection margin={'0.5rem 0 0'}>
              <InputContainer>
                <StyledInputLabel mandatory={true}>Group Selection</StyledInputLabel>
                <CustomAsyncSelect
                  className="groupSelect"
                  id="user-group-async-select"
                  placeholder="Select Group(s)"
                  onChange={(e, opt) => {
                    handleInputOnChange(e, opt);
                  }}
                  cacheOptions
                  inputValue={input}
                  onMenuScrollToBottom={() => {
                    setGroupListOffset((prev) => prev + 1);
                  }}
                  isMulti
                  isSearchable
                  isCheckboxInput
                  isClearable
                  onInputChange={onInputChange}
                  hideSelectedOptions={false}
                  loadOptions={loadOptions}
                  defaultOptions={displayedGroups}
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  value={multiValue}
                  onMenuClose={sortUnselectedGroups}
                />
              </InputContainer>
            </FormSection>
          </UserFormContainer>
          <Footer>
            <BtnContainer>
              <Button
                variant="link"
                onClick={onClose}
                borderRadius="3rem"
                borderColor={theme.colors.primaryBlue5}
                width="6.688rem"
                height="2rem"
              >
                Cancel
              </Button>
              <Button
                disabled={!hasUniqueUserEmails || checkDisable()}
                variant="hover-blue"
                borderRadius="3rem"
                bgColor={theme.colors.primaryBlue5}
                color={theme.colors.lightGrey3}
                onClick={handleUserSave}
                width="6.688rem"
                height="2rem"
              >
                Save
              </Button>
            </BtnContainer>
          </Footer>
        </Container>
      </Flyout>
    </>
  );
};

export default UserManagementModal;
